import * as React from 'react';

import numeral from 'numeral';

import type {FormattedValue} from 'nutshell-core/types';

// @ts-expect-error
import {getInstanceCurrencyPrefix} from 'shells/utils/currency';

import {ListSummaryBar} from '../entity-list/header/list-summary/list-summary-bar';
import type {Summary} from '../entity-list/header/list-summary/list-summary-stat';

import type {ReportFacet} from './report-types';

export type ReportSummaryKey =
    | 'forecastWithConfidence'
    | 'forecast'
    | 'customLead'
    | 'activity'
    | 'email'
    | 'siteAnalytics'
    | 'landingPages'
    | 'tasks'
    | 'quotes'
    | 'engagementLeads'
    | 'engagementSales'
    | 'widgetOpens'
    | 'completedActions'
    | 'visitors';

export type CustomLeadStats = {
    value: {
        mean: FormattedValue;
        sum: FormattedValue;
    };
    openedTime: {
        mean: FormattedValue;
    };
    winRate: {
        value: FormattedValue;
    };
};

export type ForecastStats = {
    sales: {
        count: FormattedValue;
        total: FormattedValue;
    };
    projected: {
        count: FormattedValue;
        total: FormattedValue;
    };
    pipeline: {
        count: FormattedValue;
        total: FormattedValue;
    };
    quota: {
        count: FormattedValue;
        total: FormattedValue;
    };
};

export type ActivityStats = {
    activities: {
        mean: FormattedValue;
        sum: FormattedValue;
    };
    duration: {
        mean: FormattedValue;
        sum: FormattedValue;
    };
};

export type EmailStats = {
    emails: {
        mean: FormattedValue;
        sum: FormattedValue;
    };
};

export type TasksStats = {
    tasks: {
        total: FormattedValue;
        completedOnTime: FormattedValue;
        completedLate: FormattedValue;
        onTimePercentage: FormattedValue;
    };
};

export type QuotesStats = {
    open: {
        count: FormattedValue;
        value: FormattedValue;
    };
    overdue: {
        count: FormattedValue;
        value: FormattedValue;
    };
};

export type SiteAnalyticsStats = {
    visitors: {
        sum: FormattedValue;
    };
    views: {
        sum: FormattedValue;
    };
    viewsPerVisit: {
        mean: FormattedValue;
    };
};

export type LandingPagesStats = {
    views: {
        sum: FormattedValue;
    };
};

const ACTIVITY_SUMMARY_SCHEMA = {
    totalActivities: {
        label: 'Total activities',
        key: 'activities',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.sum,
        type: 'integer',
    },
    ...(NutClientConfig.hasActivityReportExpansionFF
        ? {
              activitiesWithSummaries: {
                  label: 'AI-summarized activities',
                  key: 'activities',
                  // @ts-expect-error
                  getDataFromStat: (stat) => stat.withSummaries,
                  type: 'integer',
              },
              activitiesWithNotes: {
                  label: 'Activities with a note',
                  key: 'activities',
                  // @ts-expect-error
                  getDataFromStat: (stat) => stat.withNotes,
                  type: 'integer',
              },
          }
        : {}),
    avgDuration: {
        label: 'Avg activity duration',
        key: 'duration',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.mean,
        type: 'duration',
    },
    avgActivities: {
        label: 'Avg activities',
        key: 'activities',
        type: 'formattedInteger',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.mean,
    },
    totalDuration: {
        label: 'Total activity duration',
        key: 'duration',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.sum,
        type: 'duration',
    },
} as const;

const EMAIL_SUMMARY_SCHEMA = {
    totalEmails: {
        label: 'Total emails',
        key: 'emails',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.sum,
        type: 'integer',
    },
    avgEmails: {
        label: 'Avg emails sent',
        key: 'emails',
        type: 'formattedInteger',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.mean,
    },
} as const;

export const TASKS_SUMMARY_SCHEMA = {
    total: {
        label: 'Total tasks',
        key: 'tasks',
        type: 'integer',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.total,
        info: 'All tasks with a due date in the selected time period. When filtering by user: completed or skipped tasks show up for whoever completed them, while incomplete tasks show up for whoever they are assigned to.',
    },
    completedOnTime: {
        label: 'Completed on time',
        key: 'tasks',
        type: 'integer',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.completedOnTime,
    },
    completedLate: {
        label: 'Completed late',
        key: 'tasks',
        type: 'integer',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.completedLate,
    },
    onTimePercentage: {
        label: 'On time %',
        key: 'tasks',
        type: 'percent',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.onTimePercentage,
        info: 'Percentage of tasks that were finished on time. This does not include skipped tasks.',
    },
} as const;

const QUOTES_SUMMARY_SCHEMA = {
    sent: {
        label: 'Sent quotes',
        key: 'sent',
        type: 'integer',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.count,
    },
    accepted: {
        label: 'Accepted quotes',
        key: 'accepted',
        type: 'integer',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.count,
    },
} as const;

const ANALYTICS_SUMMARY_SCHEMA = {
    totalViews: {
        label: 'Total page views',
        key: 'views',
        type: 'integer',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.sum,
    },
    totalVisitors: {
        label: 'Unique visitors',
        key: 'visitors',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.sum,
        type: 'integer',
    },
    viewsPerVisit: {
        label: 'Avg views per visit',
        key: 'viewsPerVisit',
        type: 'formattedInteger',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.mean,
    },
    avgVisitors: {
        label: 'Avg # visitors',
        key: 'visitors',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.mean,
        type: 'formattedInteger',
    },
} as const;

const FORECAST_SUMMARY_SCHEMA = {
    forecastWithConfidence: {
        sales: {
            label: 'Sales to date',
            key: 'sales',
            type: 'integer',
        },
        pipelineWithConf: {
            label: 'Pipeline',
            key: 'pipelineWithConf',
            type: 'integer',
        },
        projectedWithConf: {
            label: 'Projected sales',
            key: 'projectedWithConf',
            type: 'integer',
        },
        quota: {
            label: 'Quota',
            key: 'quota',
            type: 'integer',
        },
    },
    forecast: {
        sales: {
            label: 'Sales to date',
            key: 'sales',
            type: 'integer',
        },
        pipeline: {
            label: 'Pipeline',
            key: 'pipeline',
            type: 'integer',
        },
        projected: {
            label: 'Projected sales',
            key: 'projected',
            type: 'integer',
        },
        quota: {
            label: 'Quota',
            key: 'quota',
            type: 'integer',
        },
    },
} as const;

const ENGAGEMENT_LEADS_SUMMARY_SCHEMA = {
    totalLeads: {
        label: 'Leads created',
        key: 'createdTime',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.count,
        type: 'integer',
    },
    avgPerPeriod: {
        label: 'Avg leads',
        key: 'createdTime',
        type: 'formattedInteger',
    },
} as const;

const ENGAGEMENT_SALES_SUMMARY_SCHEMA = {
    totalLeads: {
        label: 'Sales',
        key: 'createdTime',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.count,
        type: 'integer',
    },
    totalSales: {
        label: 'Total value',
        key: 'value',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.sum,
        type: 'integer',
    },
} as const;

const WIDGET_OPENS_SUMMARY_SCHEMA = {
    totalOpens: {
        label: 'Total widget opens',
        key: 'opens',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.sum,
        type: 'integer',
    },
    avgPerPeriod: {
        label: 'Avg opens',
        key: 'opens',
        type: 'formattedInteger',
    },
} as const;

const COMPLETED_ACTIONS_SUMMARY_SCHEMA = {
    totalActions: {
        label: 'Total actions',
        key: 'actions',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.sum,
        type: 'integer',
    },
    avgPerPeriod: {
        label: 'Avg actions',
        key: 'actions',
        type: 'formattedInteger',
    },
} as const;

const VISITORS_SUMMARY_SCHEMA = {
    totalVisitors: {
        label: 'Unique visitors',
        key: 'visitors',
        // @ts-expect-error
        getDataFromStat: (stat) => stat.sum,
        type: 'integer',
    },
    avgPerPeriod: {
        label: 'Avg visitors',
        key: 'visitors',
        type: 'formattedInteger',
    },
} as const;

const SUMMARY_SCHEMA = {
    count: {
        totalLeads: {
            label: 'Total leads',
            key: 'createdTime',
            // @ts-expect-error
            getDataFromStat: (stat) => stat.count,
            type: 'integer',
        },
        avgPerPeriod: {
            label: 'Avg leads',
            key: 'createdTime',
            type: 'formattedInteger',
        },
        openedTime: {
            label: 'Avg time open',
            key: 'openedTime',
            // @ts-expect-error
            getDataFromStat: (stat) => stat.mean,
            type: 'date',
        },
        winRate: {
            label: 'Win rate',
            key: 'winRate',
            // @ts-expect-error
            getDataFromStat: (stat) => stat.value,
            type: 'percent',
        },
    },
    total: {
        totalValue: {
            label: 'Total value',
            key: 'value',
            // @ts-expect-error
            getDataFromStat: (stat) => stat.sum,
            type: 'integer',
        },
        avgPerPeriod: {
            label: 'Avg value',
            key: 'value',
            type: 'formattedInteger',
        },
        openedTime: {
            label: 'Avg time open',
            key: 'openedTime',
            // @ts-expect-error
            getDataFromStat: (stat) => stat.mean,
            type: 'date',
        },
        avgValue: {
            label: 'Avg lead value',
            key: 'value',
            // @ts-expect-error
            getDataFromStat: (stat) => stat.mean,
            type: 'formattedInteger',
        },
    },
} as const;

type ReportSummaryProps = {
    summaryType: ReportSummaryKey;
    leadStatuses: string[];
    chartBucketCount: number;
    stats: any;
    facet?: ReportFacet;
    gapLabel?: string;
    isLoading?: boolean;
};

export class ReportSummary extends React.Component<ReportSummaryProps> {
    static defaultProps = {
        leadStatuses: ['Open'],
    };

    render() {
        const summaries = getSummarySchema(
            this.props.summaryType,
            this.props.facet,
            this.props.gapLabel,
            this.props.chartBucketCount
        );

        if (!summaries) return null;

        return (
            <ListSummaryBar
                summaries={summaries}
                stats={this.props.stats}
                appliedStatuses={this.props.leadStatuses}
                isUnderlined={true}
                isFirstLoad={Boolean(this.props.isLoading)}
                isFiltering={false}
            />
        );
    }
}

function getSummarySchema(
    type: ReportSummaryKey,
    facet: ReportFacet | null | undefined,
    gapLabel: string | null | undefined,
    chartBucketCount: number
) {
    // Server does the heavy lifting here,
    // and the activity report doens't have a count/total toggle, so no
    // need to worry about the different facets no faceting on the client
    if (type === 'activity') {
        return getActivitySummarySchema(gapLabel);
    }

    if (type === 'email') {
        return Object.keys(EMAIL_SUMMARY_SCHEMA).reduce<Record<string, any>>((schema, key) => {
            // We need to calculate average per period, we don't get it from the server
            if (key === 'avgEmails' && gapLabel) {
                schema[key] = {
                    ...EMAIL_SUMMARY_SCHEMA[key],
                    label: `${EMAIL_SUMMARY_SCHEMA[key].label} per ${gapLabel}`,
                };
            } else {
                // @ts-expect-error
                schema[key] = EMAIL_SUMMARY_SCHEMA[key];
            }

            return schema;
        }, {});
    }

    if (type === 'tasks') {
        return TASKS_SUMMARY_SCHEMA;
    }

    if (type === 'quotes') {
        return QUOTES_SUMMARY_SCHEMA;
    }

    if (type === 'engagementLeads') {
        return Object.keys(ENGAGEMENT_LEADS_SUMMARY_SCHEMA).reduce<Record<string, any>>(
            (schema, key) => {
                // We need to calculate average per period, we don't get it from the server
                if (key === 'avgPerPeriod' && gapLabel) {
                    schema[key] = {
                        ...ENGAGEMENT_LEADS_SUMMARY_SCHEMA[key],
                        label: `${ENGAGEMENT_LEADS_SUMMARY_SCHEMA[key].label} per ${gapLabel}`,
                        // @ts-expect-error
                        getDataFromStat: (stat) => {
                            const rawAvg = stat.count.value / chartBucketCount;
                            const avg = isNaN(rawAvg) ? 0 : rawAvg;

                            return {
                                value: avg,
                                formatted: `${numeral(avg).format('0[.]0')}`,
                                suffix: '',
                                prefix: '',
                            };
                        },
                    };
                } else {
                    // @ts-expect-error
                    schema[key] = ENGAGEMENT_LEADS_SUMMARY_SCHEMA[key];
                }

                return schema;
            },
            {}
        );
    }

    if (type === 'engagementSales') {
        return ENGAGEMENT_SALES_SUMMARY_SCHEMA;
    }

    if (type === 'widgetOpens') {
        return Object.keys(WIDGET_OPENS_SUMMARY_SCHEMA).reduce<Record<string, any>>(
            (schema, key) => {
                if (key === 'avgPerPeriod' && gapLabel) {
                    schema[key] = {
                        ...WIDGET_OPENS_SUMMARY_SCHEMA[key],
                        label: `${WIDGET_OPENS_SUMMARY_SCHEMA[key].label} per ${gapLabel}`,
                        // @ts-expect-error
                        getDataFromStat: (stat) => {
                            const rawAvg = stat.sum.value / chartBucketCount;
                            const avg = isNaN(rawAvg) ? 0 : rawAvg;

                            return {
                                value: avg,
                                formatted: `${numeral(avg).format('0[.]0')}`,
                                suffix: '',
                                prefix: '',
                            };
                        },
                    };
                } else {
                    // @ts-expect-error
                    schema[key] = WIDGET_OPENS_SUMMARY_SCHEMA[key];
                }

                return schema;
            },
            {}
        );
    }

    if (type === 'completedActions') {
        return Object.keys(COMPLETED_ACTIONS_SUMMARY_SCHEMA).reduce<Record<string, any>>(
            (schema, key) => {
                if (key === 'avgPerPeriod' && gapLabel) {
                    schema[key] = {
                        ...COMPLETED_ACTIONS_SUMMARY_SCHEMA[key],
                        label: `${COMPLETED_ACTIONS_SUMMARY_SCHEMA[key].label} per ${gapLabel}`,
                        // @ts-expect-error
                        getDataFromStat: (stat) => {
                            const rawAvg = stat.sum.value / chartBucketCount;
                            const avg = isNaN(rawAvg) ? 0 : rawAvg;

                            return {
                                value: avg,
                                formatted: `${numeral(avg).format('0[.]0')}`,
                                suffix: '',
                                prefix: '',
                            };
                        },
                    };
                } else {
                    // @ts-expect-error
                    schema[key] = COMPLETED_ACTIONS_SUMMARY_SCHEMA[key];
                }

                return schema;
            },
            {}
        );
    }

    if (type === 'visitors') {
        return Object.keys(VISITORS_SUMMARY_SCHEMA).reduce<Record<string, any>>((schema, key) => {
            if (key === 'avgPerPeriod' && gapLabel) {
                schema[key] = {
                    ...VISITORS_SUMMARY_SCHEMA[key],
                    label: `${VISITORS_SUMMARY_SCHEMA[key].label} per ${gapLabel}`,
                    // @ts-expect-error
                    getDataFromStat: (stat) => {
                        const rawAvg = stat.sum.value / chartBucketCount;
                        const avg = isNaN(rawAvg) ? 0 : rawAvg;

                        return {
                            value: avg,
                            formatted: `${numeral(avg).format('0,0')}`,
                            suffix: '',
                            prefix: '',
                        };
                    },
                };
            } else {
                // @ts-expect-error
                schema[key] = VISITORS_SUMMARY_SCHEMA[key];
            }

            return schema;
        }, {});
    }

    if (type === 'siteAnalytics') {
        return Object.keys(ANALYTICS_SUMMARY_SCHEMA).reduce<Record<string, any>>((schema, key) => {
            if (key === 'avgVisitors' && gapLabel) {
                schema[key] = {
                    ...ANALYTICS_SUMMARY_SCHEMA[key],
                    label: `${ANALYTICS_SUMMARY_SCHEMA[key].label} per ${gapLabel}`,
                    // @ts-expect-error
                    getDataFromStat: (stat) => {
                        const rawAvg = stat.sum.value / chartBucketCount;
                        const avg = isNaN(rawAvg) ? 0 : rawAvg;

                        return {
                            value: avg,
                            formatted: `${numeral(avg).format('0,0')}`,
                            suffix: '',
                            prefix: '',
                        };
                    },
                };
            } else {
                // @ts-expect-error
                schema[key] = ANALYTICS_SUMMARY_SCHEMA[key];
            }

            return schema;
        }, {});
    }

    // Need to add getter function for forecast and forecastWithConfidence, depending on count/total
    if (type === 'forecast' || type === 'forecastWithConfidence') {
        return Object.keys(FORECAST_SUMMARY_SCHEMA[type]).reduce<Record<string, any>>(
            (schema, key) => {
                schema[key] = {
                    // @ts-expect-error
                    ...FORECAST_SUMMARY_SCHEMA[type][key],
                    // @ts-expect-error
                    getDataFromStat: (stat) => stat[facet],
                };

                return schema;
            },
            {}
        );
    }

    if (facet) {
        const isTotal = facet === 'total';
        const format = isTotal ? '0,0' : '0[.]0';

        return Object.keys(SUMMARY_SCHEMA[facet]).reduce<Record<string, any>>((schema, key) => {
            // We need to calculate average per period, we don't get it from the server
            if (key === 'avgPerPeriod' && gapLabel) {
                schema[key] = {
                    ...SUMMARY_SCHEMA[facet][key],
                    label: `${SUMMARY_SCHEMA[facet][key].label} per ${gapLabel}`,
                    // @ts-expect-error
                    getDataFromStat: (stat) => {
                        const rawAvg = isTotal
                            ? stat.sum.value / chartBucketCount
                            : stat.count.value / chartBucketCount;

                        const avg = isNaN(rawAvg) ? 0 : rawAvg;
                        const prefix = isTotal
                            ? (stat.sum && stat.sum.prefix) || getInstanceCurrencyPrefix()
                            : '';

                        return {
                            value: avg,
                            formatted: `${numeral(avg).format(format)}`,
                            suffix: '',
                            prefix,
                        };
                    },
                };
            } else {
                // @ts-expect-error
                schema[key] = SUMMARY_SCHEMA[facet][key];
            }

            return schema;
        }, {});
    }
}

export function getActivitySummarySchema(gapLabel?: string | null): {
    [key: string]: Summary;
} {
    return Object.keys(ACTIVITY_SUMMARY_SCHEMA).reduce<Record<string, any>>((schema, key) => {
        // We need to calculate average per period, we don't get it from the server
        if (key === 'avgActivities' && gapLabel) {
            schema[key] = {
                ...ACTIVITY_SUMMARY_SCHEMA[key],
                label: `${ACTIVITY_SUMMARY_SCHEMA[key].label} per ${gapLabel}`,
            };
        } else {
            // @ts-expect-error
            schema[key] = ACTIVITY_SUMMARY_SCHEMA[key];
        }

        return schema;
    }, {});
}
